import { useEffect, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { RotateCcw, Search, Users } from 'lucide-react'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Skeleton } from '@quebon/ui/components/skeleton'
import { lcTimeslotApi } from '@/api/lc'
import { AdminSearchInput } from '@/components/admin/common/AdminSearchInput'
import { AdminSelect, type AdminSelectOption } from '@/components/admin/common/AdminSelect'
import { AdminPagination } from '@/components/admin/members/AdminPagination'
import ClassRosterPanel from './ClassRosterPanel'

type RosterStatus = 'ALL' | 'ENROLLED' | 'ON_LEAVE'

interface Props {
  trialEnabled?: boolean
  active?: boolean
}

const PAGE_SIZE = 20
const STATUS_VALUES: RosterStatus[] = ['ALL', 'ENROLLED', 'ON_LEAVE']

export default function ClassRosterSearchPanel({ trialEnabled = false, active = true }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const [searchInput, setSearchInput] = useState('')
  const [statusInput, setStatusInput] = useState<RosterStatus>('ALL')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [appliedStatus, setAppliedStatus] = useState<RosterStatus>('ALL')
  const [page, setPage] = useState(0)
  const statusOptions: AdminSelectOption[] = STATUS_VALUES.map((status) => ({
    value: status,
    label: t(`classes.rosterSearch.status.${status}`),
  }))

  const rosterQuery = useQuery({
    queryKey: ['lcRosterSearch', { q: appliedSearch, status: appliedStatus, page }],
    queryFn: () => lcTimeslotApi.searchRoster({
      q: appliedSearch || undefined,
      status: appliedStatus === 'ALL' ? undefined : appliedStatus,
      page,
      size: PAGE_SIZE,
    }),
    enabled: active,
  })
  const response = rosterQuery.data?.data?.data

  useEffect(() => {
    if (response == null || page === 0 || page < response.totalPages) return
    setPage(Math.max(0, response.totalPages - 1))
  }, [page, response])

  const hasDraftChanges = searchInput.trim() !== appliedSearch || statusInput !== appliedStatus
  const canSubmit = hasDraftChanges && (searchInput.trim().length > 0 || statusInput !== 'ALL')

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const query = searchInput.trim()
    if (!query && statusInput === 'ALL') return
    setSearchInput(query)
    setAppliedSearch(query)
    setAppliedStatus(statusInput)
    setPage(0)
  }

  const resetSearch = () => {
    setSearchInput('')
    setStatusInput('ALL')
    setAppliedSearch('')
    setAppliedStatus('ALL')
    setPage(0)
  }

  return (
    <div className="flex flex-col gap-5">
      <form onSubmit={submitSearch}>
        <FieldGroup className="gap-3">
          <Field orientation="responsive">
            <FieldLabel htmlFor="class-roster-search" className="sr-only">
              {t('classes.rosterSearch.searchLabel')}
            </FieldLabel>
            <AdminSearchInput
              id="class-roster-search"
              value={searchInput}
              maxLength={100}
              onValueChange={setSearchInput}
              onClear={() => setSearchInput('')}
              clearLabel={t('classes.rosterSearch.clearSearch')}
              placeholder={t('classes.rosterSearch.searchPlaceholder')}
              containerClassName="@md/field-group:max-w-md"
            />
            <AdminSelect
              options={statusOptions}
              value={statusInput}
              onValueChange={(value) => setStatusInput(value as RosterStatus)}
              ariaLabel={t('classes.rosterSearch.statusLabel')}
              className="w-36"
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={!canSubmit}>
                <Search data-icon="inline-start" />
                {t('classes.rosterSearch.search')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!searchInput && statusInput === 'ALL' && !appliedSearch && appliedStatus === 'ALL'}
                onClick={resetSearch}
              >
                <RotateCcw data-icon="inline-start" />
                {t('classes.rosterSearch.reset')}
              </Button>
            </div>
          </Field>
        </FieldGroup>
      </form>

      {rosterQuery.isLoading ? (
        <div className="flex flex-col gap-2" role="status" aria-label={t('common:label.loading')}>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : rosterQuery.error ? (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>{t('classes.rosterSearch.loadError')}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => rosterQuery.refetch()}>
              {t('classes.rosterSearch.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : response == null || response.content.length === 0 ? (
        <Alert>
          <Users />
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>{t('classes.rosterSearch.empty')}</span>
            {(appliedSearch || appliedStatus !== 'ALL') ? (
              <Button type="button" size="sm" variant="outline" onClick={resetSearch}>
                {t('classes.rosterSearch.reset')}
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {t('classes.rosterSearch.total', { count: response.totalElements })}
          </p>
          <ClassRosterPanel
            students={response.content}
            showClassColumn
            showHeader={false}
            maxHeightClassName="max-h-none"
            trialEnabled={trialEnabled}
          />
          <AdminPagination page={page} totalPages={response.totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  )
}
